import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useParams } from "react-router-dom";

import {
  Alert,
  Badge,
  Caption,
  DetailedSubheading,
  Divider,
  Flex,
  SectionHeading,
} from "@olist/design-system";
import { ChatOutline } from "@olist/design-system-icons";
import { PageLoading } from "@olist/templates";

import { Attachments } from "./components/attachments";
import { Discussion } from "./components/discussion";
import { MessageTextArea } from "./components/text-area";
import {
  localTicket,
  productToName,
  statutsFiltersDictionary,
} from "./constants";
import { useGetTicketData, useTicketComment } from "./hooks";
import {
  Description,
  LoadingWrapper,
  TicketCard,
  TicketCardBody,
  TicketCardHeader,
  TicketDetailsCard,
  TicketDiscussion,
  TicketDiscussionCardBody,
  TicketInfo,
  TicketInfoLabel,
  TicketInfoValue,
  TicketMessage,
  TicketWrapper,
} from "./styles";
import { formatDateTime } from "../../helpers/dateUtils";
import { Cactus, InternalServerError } from "../../icons";
import { ErrorHandling } from "components/errorHandling";
import { useToast } from "components/toast";
import { MixpanelContext } from "contexts/mixpanel";

const isDevelopment =
  (process.env.REACT_APP_NODE_ENV ?? "production") === "development";

const statutsFiltersBadgeVariation = {
  novo: "info",
  aberto: "info",
  pendente: "alert",
  escalado: "info",
  fechado: "success",
  concluído: "success",
  resolvido: "info",
};

export function Ticket() {
  const [isSeeMoreClicked, setIsSeeMoreClicked] = useState(true);
  const { id: ticketId } = useParams<{ id: string }>();
  const { trackAgent } = useContext(MixpanelContext);

  const { handleOpen } = useToast();

  const {
    isError,
    isLoading,
    ticket: ticketData,
  } = useGetTicketData({
    id: ticketId,
  });

  const createCommentSuccessed = () => {
    trackAgent.track("helpcenter_enviou_comentario_ticket", { ticketId });
    handleOpen({
      message:
        "Comentário criado com sucesso, atualizaremos sua página para visualizar em alguns instantes",
      variation: "success",
    });
  };

  const createCommentFailed = () => {
    handleOpen({
      message: "Tivemos um problema, tente novamente mais tarde",
      variation: "alert",
    });
  };

  const { createComment, isLoading: isCreateCommentLoading } = useTicketComment(
    {
      onSuccess: createCommentSuccessed,
      onError: createCommentFailed,
    }
  );

  const handleCreateComment = useCallback(
    ({
      files,
      message,
    }: {
      message: string;
      files?: Array<{ base64: string; filename: string; filetype: string }>;
    }) => {
      if (ticketData?.isClosed) {
        return;
      }

      let attachment:
        | Array<{ base64: string; filetype: string; filename: string }>
        | undefined;
      if (files) {
        attachment = files;
      }

      createComment({
        id: ticketId || "",
        comment: {
          comment: message,
          attachment,
        },
      });
    },
    [createComment, ticketId]
  );

  const ticket = isDevelopment ? localTicket : ticketData;

  const commentSection = useMemo(() => {
    if (ticketData?.isClosed) return null;
    return (
      <TicketCard>
        <TicketDiscussionCardBody>
          <TicketMessage>
            <MessageTextArea
              onSubmit={(message, files) =>
                handleCreateComment({ message, files })
              }
              isLoading={isCreateCommentLoading}
            />
          </TicketMessage>
        </TicketDiscussionCardBody>
      </TicketCard>
    );
  }, [handleCreateComment, ticketData, isCreateCommentLoading]);

  const handleSeeMoreItensClicked = () => {
    const isClicked = !isSeeMoreClicked;
    setIsSeeMoreClicked(isClicked);
  };

  // const handleDownloadFile = async (base64, name, mime_type) => {
  //   try {
  //     await downloadBase64Image(base64, name, mime_type);
  //     console.log('Download feito com sucesso');
  //   } catch (error) {
  //     handleOpen({
  //       message: "Tivemos um problema, tente novamente mais tarde",
  //       variation: "alert",
  //     });
  //   }
  // }

  useEffect(() => {
    const ticketLastCommentElement = document.getElementById(
      "discussion-last-comment"
    );

    if (ticketLastCommentElement) {
      ticketLastCommentElement.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [ticket]);

  useEffect(() => {
    if (ticketId) {
      trackAgent.track("helpcenter_acessou_detalhesticket", { ticketId });
    }
  }, []);

  if (!isDevelopment && isLoading) {
    return (
      <LoadingWrapper>
        <PageLoading />
      </LoadingWrapper>
    );
  }

  if (!isDevelopment && isError) {
    return (
      <>
        {process.env.REACT_APP_IS_SALESFORCE_INSTABLE && (
          <Alert
            showIcon
            variation="error"
            title="Atenção:"
            description="No momento estamos com instabilidade nessa página. Já estamos trabalhando para restabelecer a visualização dos chamados o mais rápido possível. No entanto, você pode continuar acompanhando e interagindo por e-mail normalmente. Agradecemos pela compreensão."
          />
        )}
        <ErrorHandling
          image={<InternalServerError />}
          title="Erro ao buscar chamado"
          subtitle="contudo nós fomos notificados, o problema será resolvido em breve!"
        />
      </>
    );
  }

  if (!ticket) {
    return (
      <ErrorHandling
        image={<Cactus />}
        title="Chamado não encontrado"
        subtitle="verifique o número do chamado e tente novamente"
      />
    );
  }

  return (
    <TicketWrapper>
      <Flex mb={16} width="100%">
        <Flex flexDirection="column" gap="stack.medium" width="70%">
          {ticketData?.isClosed && (
            <Flex width="98%">
              <Alert
                variation="alert"
                showIcon
                title="Este chamado foi encerrado."
                description="Se precisar falar com a gente, é só abrir um novo chamado. Nossa equipe está pronta para te ajudar!"
              />
            </Flex>
          )}
          {ticketData?.status.toLocaleLowerCase() === "resolvido" && (
            <Flex width="98%">
              <Alert
                variation="info"
                showIcon
                title="Este chamado foi finalizado"
                description="Caso sua solicitação não tenha sido totalmente resolvida, você pode nos responder aqui mesmo nas próximas 24 horas. Após esse prazo, será preciso abrir um novo chamado."
              />
            </Flex>
          )}
          <TicketCard>
            <TicketCardHeader>
              <SectionHeading>{ticket?.title}</SectionHeading>
            </TicketCardHeader>
            <TicketCardBody>
              <Flex flexDirection="column" gap="stack.small">
                <Caption bold>Descrição</Caption>
                <Description
                  id="description"
                  backgroundColor="neutral.softer"
                  padding="inset.small"
                >
                  {ticket?.description}
                </Description>
              </Flex>
            </TicketCardBody>
          </TicketCard>

          <TicketCard>
            <TicketDiscussionCardBody>
              <TicketDiscussion>
                <Discussion
                  discussion={ticket.discussion || []}
                  contact_email={ticket.contact_email}
                />
              </TicketDiscussion>
            </TicketDiscussionCardBody>
          </TicketCard>
          {commentSection}
        </Flex>
        <Flex flexDirection="column" gap="stack.medium" width="30%">
          <TicketDetailsCard>
            <TicketCardHeader>
              <SectionHeading>Informações</SectionHeading>
            </TicketCardHeader>
            <TicketCardBody>
              <TicketInfo>
                <TicketInfoLabel>E-mail</TicketInfoLabel>
                <TicketInfoValue>{ticket?.contact_email}</TicketInfoValue>
              </TicketInfo>
              <Divider />
              <DetailedSubheading my="stack.small">
                Sobre seu chamado
              </DetailedSubheading>
              <TicketInfo>
                <TicketInfoLabel>Nº do chamado</TicketInfoLabel>
                <TicketInfoValue>{ticket.number}</TicketInfoValue>
              </TicketInfo>
              <TicketInfo>
                <TicketInfoLabel>Criado em</TicketInfoLabel>
                <TicketInfoValue>
                  {formatDateTime(ticket.created_at)}
                </TicketInfoValue>
              </TicketInfo>
              <TicketInfo>
                <TicketInfoLabel>Atualizado em</TicketInfoLabel>
                <TicketInfoValue>
                  {formatDateTime(ticket.updated_at)}
                </TicketInfoValue>
              </TicketInfo>
              <TicketInfo>
                <TicketInfoLabel>Produto</TicketInfoLabel>
                <TicketInfoValue>
                  {productToName[ticket?.product || "store"]}
                </TicketInfoValue>
              </TicketInfo>
              <TicketInfo>
                <TicketInfoLabel>Status</TicketInfoLabel>
                <TicketInfoValue>
                  <Badge
                    variation={
                      statutsFiltersBadgeVariation[
                        ticket?.status.toLocaleLowerCase()
                      ].variation
                    }
                    showIcon={false}
                  >
                    {
                      statutsFiltersDictionary[
                        ticket?.status.toLocaleLowerCase()
                      ].label
                    }
                  </Badge>
                </TicketInfoValue>
              </TicketInfo>
            </TicketCardBody>
          </TicketDetailsCard>
          <TicketDetailsCard>
            <TicketCardHeader>
              <SectionHeading>Arquivos compartilhados</SectionHeading>
            </TicketCardHeader>
            <TicketCardBody>
              <Attachments
                attachments={ticket?.attachments || []}
                handleSeeMoreItensClicked={handleSeeMoreItensClicked}
                isSeeMoreClicked={isSeeMoreClicked}
              />
            </TicketCardBody>
          </TicketDetailsCard>
          <TicketDetailsCard>
            <TicketCardHeader>
              <SectionHeading>Atendimento</SectionHeading>
            </TicketCardHeader>
            <TicketCardBody>
              <Flex gap="inline.1xsmall">
                <ChatOutline />
                <Caption>
                  Segunda à sexta das 8h30 às 11h30 e das 13h30 às 17h30
                </Caption>
              </Flex>
            </TicketCardBody>
          </TicketDetailsCard>
        </Flex>
      </Flex>
    </TicketWrapper>
  );
}
